import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/utils-domain";
import { Plus, FileText, Robot, MagnifyingGlassPlus, ShieldWarning, X, DownloadSimple, Scroll } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Documents() {
    const [docs, setDocs] = useState([]);
    const [cases, setCases] = useState([]);
    const [open, setOpen] = useState(false);
    const [genOpen, setGenOpen] = useState(false);
    const [activeDoc, setActiveDoc] = useState(null);
    const [aiMode, setAiMode] = useState(null); // 'summary' | 'review'
    const [aiText, setAiText] = useState("");
    const [aiBusy, setAiBusy] = useState(false);

    const load = async () => {
        const [d, c] = await Promise.all([api.get("/documents"), api.get("/cases")]);
        setDocs(d.data); setCases(c.data);
    };
    useEffect(() => { load(); }, []);

    const openDoc = async (id, mode) => {
        const { data } = await api.get(`/documents/${id}`);
        setActiveDoc(data);
        setAiMode(mode || null);
        setAiText(mode === "summary" ? data.ai_summary || "" : mode === "review" ? data.ai_review || "" : "");
    };

    const runAi = async (mode) => {
        if (!activeDoc) return;
        setAiBusy(true);
        setAiMode(mode);
        try {
            const url = mode === "summary" ? "/ai/summarize" : "/ai/contract-review";
            const { data } = await api.post(url, { document_id: activeDoc.document_id });
            setAiText(mode === "summary" ? data.summary : data.review);
            toast.success(`AI ${mode === "summary" ? "summary" : "contract review"} ready`);
        } catch {
            toast.error("AI request failed");
        } finally {
            setAiBusy(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto animate-fade-up">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Document Hub</div>
                    <h1 className="font-display text-4xl font-extrabold tracking-tight">Documents</h1>
                    <p className="text-sm text-slate-500 mt-2">{docs.length} files · AI-powered review</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setGenOpen(true)} className="btn-secondary inline-flex items-center gap-2" data-testid="generate-document-button">
                        <Scroll size={14} weight="duotone" /> Generate document
                    </button>
                    <button onClick={() => setOpen(true)} className="btn-primary inline-flex items-center gap-2" data-testid="new-document-button">
                        <Plus size={14} weight="bold" /> Add document
                    </button>
                </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {docs.map((d) => (
                    <div key={d.document_id} className="card-sharp p-5 group hover:border-[#0B2447] transition" data-testid={`doc-card-${d.document_id}`}>
                        <div className="flex items-center gap-2">
                            <FileText size={20} weight="duotone" className="text-[#0B2447]" />
                            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{d.doc_type}</div>
                        </div>
                        <div className="font-semibold text-sm mt-3 line-clamp-2 leading-snug">{d.name}</div>
                        <div className="text-[10px] font-mono text-slate-400 mt-2">{fmtDate(d.created_at)} · {Math.round((d.size || 0) / 1024)} KB</div>
                        <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                            <button onClick={() => openDoc(d.document_id, "summary")} className="text-[11px] font-semibold text-[#0B2447] hover:underline inline-flex items-center gap-1" data-testid={`ai-summary-${d.document_id}`}>
                                <Robot size={12} weight="duotone" /> Summarize
                            </button>
                            <button onClick={() => openDoc(d.document_id, "review")} className="text-[11px] font-semibold text-purple-700 hover:underline inline-flex items-center gap-1" data-testid={`ai-review-${d.document_id}`}>
                                <ShieldWarning size={12} weight="duotone" /> Contract review
                            </button>
                            <button onClick={() => openDoc(d.document_id)} className="text-[11px] font-semibold text-slate-600 hover:underline inline-flex items-center gap-1 ml-auto">
                                <MagnifyingGlassPlus size={12} /> Open
                            </button>
                        </div>
                    </div>
                ))}
                {docs.length === 0 && (
                    <div className="col-span-full card-sharp p-12 text-center text-sm text-slate-500">
                        No documents yet. Click "Add document" or "Generate document" to start.
                    </div>
                )}
            </div>

            {open && <UploadModal cases={cases} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
            {genOpen && <GenerateModal cases={cases} onClose={() => setGenOpen(false)} />}
            {activeDoc && (
                <DocPanel
                    doc={activeDoc}
                    aiMode={aiMode}
                    aiText={aiText}
                    aiBusy={aiBusy}
                    runAi={runAi}
                    onClose={() => { setActiveDoc(null); setAiMode(null); setAiText(""); load(); }}
                />
            )}
        </div>
    );
}

function UploadModal({ cases, onClose, onSaved }) {
    const [form, setForm] = useState({ name: "", doc_type: "Contract", case_id: "", content: "" });

    const onFile = async (e) => {
        const f = e.target.files[0];
        if (!f) return;
        const text = await f.text();
        setForm((s) => ({ ...s, name: f.name, content: text }));
    };
    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/documents", form);
            toast.success("Document added");
            onSaved();
        } catch { toast.error("Upload failed"); }
    };

    return (
        <Modal title="Add document" onClose={onClose}>
            <form onSubmit={submit} className="space-y-4" data-testid="upload-doc-form">
                <div>
                    <label className="label-sharp">Upload text/PDF/markdown</label>
                    <input type="file" accept=".txt,.md,.pdf,.doc,.docx" onChange={onFile} className="input-sharp file:mr-3 file:border-0 file:bg-slate-100 file:px-3 file:py-1 file:rounded-sm file:text-xs" data-testid="upload-doc-file" />
                    <div className="text-[10px] text-slate-500 mt-1 font-mono">Text-based files preferred for AI analysis</div>
                </div>
                <div><label className="label-sharp">Name</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-sharp" data-testid="upload-doc-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="label-sharp">Type</label>
                        <select value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} className="input-sharp">
                            {["Contract", "Pleading", "Discovery", "Correspondence", "General"].map((s) => <option key={s}>{s}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="label-sharp">Case (optional)</label>
                        <select value={form.case_id} onChange={(e) => setForm({ ...form, case_id: e.target.value })} className="input-sharp">
                            <option value="">— none —</option>
                            {cases.map((c) => <option key={c.case_id} value={c.case_id}>{c.name}</option>)}
                        </select>
                    </div>
                </div>
                <div>
                    <label className="label-sharp">…or paste content directly</label>
                    <textarea rows={5} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="input-sharp font-mono text-xs" placeholder="Paste contract or document text here…" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                    <button type="submit" className="btn-primary" data-testid="upload-doc-submit">Add document</button>
                </div>
            </form>
        </Modal>
    );
}

function GenerateModal({ cases, onClose }) {
    const [form, setForm] = useState({ template: "Retainer Agreement", client_name: "", case_name: "", fee_structure: "Hourly at $300/hr", additional_terms: "" });
    const [content, setContent] = useState("");
    const [busy, setBusy] = useState(false);

    const run = async (e) => {
        e.preventDefault();
        setBusy(true); setContent("");
        try {
            const { data } = await api.post("/ai/generate-document", form);
            setContent(data.content);
        } catch { toast.error("Generation failed"); }
        finally { setBusy(false); }
    };
    const download = () => {
        const blob = new Blob([content], { type: "text/markdown" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${form.template.replace(/\s+/g, "_")}_${form.client_name || "Draft"}.md`;
        a.click();
    };

    return (
        <Modal title="Generate document" onClose={onClose} wide>
            <div className="grid lg:grid-cols-2 gap-6">
                <form onSubmit={run} className="space-y-4" data-testid="gen-doc-form">
                    <div>
                        <label className="label-sharp">Template</label>
                        <select value={form.template} onChange={(e) => setForm({ ...form, template: e.target.value })} className="input-sharp" data-testid="gen-template-select">
                            {["Retainer Agreement", "NDA", "Engagement Letter", "Demand Letter"].map((s) => <option key={s}>{s}</option>)}
                        </select>
                    </div>
                    <div><label className="label-sharp">Client name</label><input required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="input-sharp" data-testid="gen-client-input" /></div>
                    <div>
                        <label className="label-sharp">Matter (optional)</label>
                        <select value={form.case_name} onChange={(e) => setForm({ ...form, case_name: e.target.value })} className="input-sharp">
                            <option value="">— general —</option>
                            {cases.map((c) => <option key={c.case_id} value={c.name}>{c.name}</option>)}
                        </select>
                    </div>
                    <div><label className="label-sharp">Fee structure</label><input value={form.fee_structure} onChange={(e) => setForm({ ...form, fee_structure: e.target.value })} className="input-sharp" /></div>
                    <div><label className="label-sharp">Additional terms</label><textarea rows={3} value={form.additional_terms} onChange={(e) => setForm({ ...form, additional_terms: e.target.value })} className="input-sharp" /></div>
                    <button disabled={busy} type="submit" className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="gen-doc-submit">
                        <Robot size={14} weight="duotone" /> {busy ? "Drafting with Claude…" : "Generate"}
                    </button>
                </form>
                <div className="border border-slate-200 bg-slate-50 rounded-sm p-4 min-h-[300px] max-h-[60vh] overflow-y-auto">
                    {!content && !busy && <div className="text-sm text-slate-500 font-mono">Output will appear here.</div>}
                    {busy && <div className="text-sm text-slate-500 font-mono">Claude is drafting…</div>}
                    {content && (
                        <>
                            <div className="flex justify-end mb-3">
                                <button onClick={download} className="btn-secondary inline-flex items-center gap-2" data-testid="gen-doc-download">
                                    <DownloadSimple size={14} /> Download
                                </button>
                            </div>
                            <pre className="text-xs text-slate-800 whitespace-pre-wrap font-mono leading-relaxed">{content}</pre>
                        </>
                    )}
                </div>
            </div>
        </Modal>
    );
}

function DocPanel({ doc, aiMode, aiText, aiBusy, runAi, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl bg-white h-full overflow-y-auto animate-fade-up" data-testid="doc-side-panel">
                <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">{doc.doc_type}</div>
                        <div className="font-display text-xl font-bold mt-0.5 line-clamp-1">{doc.name}</div>
                    </div>
                    <button onClick={onClose}><X size={20} /></button>
                </div>
                <div className="grid lg:grid-cols-2 divide-x divide-slate-200 min-h-[calc(100vh-65px)]">
                    <div className="p-6 overflow-y-auto">
                        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-3">Content</div>
                        <pre className="text-xs text-slate-800 whitespace-pre-wrap font-mono leading-relaxed">{doc.content || "(empty)"}</pre>
                    </div>
                    <div className="p-6 bg-slate-50">
                        <div className="flex gap-2 mb-4">
                            <button onClick={() => runAi("summary")} className={`btn-secondary inline-flex items-center gap-2 ${aiMode === "summary" ? "bg-slate-900 text-white border-slate-900" : ""}`} data-testid="panel-summarize">
                                <Robot size={14} weight="duotone" /> Summarize
                            </button>
                            <button onClick={() => runAi("review")} className={`btn-secondary inline-flex items-center gap-2 ${aiMode === "review" ? "bg-slate-900 text-white border-slate-900" : ""}`} data-testid="panel-review">
                                <ShieldWarning size={14} weight="duotone" /> Contract review
                            </button>
                        </div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-3">
                            {aiMode === "summary" ? "AI Summary" : aiMode === "review" ? "AI Contract Review" : "AI Output"}
                        </div>
                        {aiBusy && <div className="text-sm text-slate-500 font-mono">Claude is reading…</div>}
                        {!aiBusy && !aiText && <div className="text-sm text-slate-500 font-mono">Pick an action above to run AI.</div>}
                        {aiText && (
                            <div className="prose prose-sm prose-slate max-w-none">
                                <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed text-slate-800">{aiText}</pre>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Modal({ title, onClose, children, wide }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
            <div className={`bg-white rounded-sm border border-slate-200 shadow-xl w-full ${wide ? "max-w-4xl" : "max-w-lg"} p-7 max-h-[90vh] overflow-y-auto animate-fade-up`}>
                <div className="flex items-center justify-between mb-5">
                    <h3 className="font-display text-2xl font-bold">{title}</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                {children}
            </div>
        </div>
    );
}
